// Header announcements: feed state, per-user viewed state, the
// modal, and live updates. Lives above the router so the header banner (which
// remounts with every MainLayout) doesn't refetch or reconnect on navigation.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import * as api from '../services/announcementService';

const AnnouncementContext = createContext(null);

const POLL_MS = 2 * 60 * 1000; // picks up scheduled start/expiry times

const LEVEL_RANK = { urgent: 0, warning: 1, celebration: 2, update: 3 };

// Urgent first, then pinned, then newest.
const byPriority = (a, b) =>
  (LEVEL_RANK[a.level] ?? 9) - (LEVEL_RANK[b.level] ?? 9)
  || (b.is_pinned ? 1 : 0) - (a.is_pinned ? 1 : 0)
  || new Date(b.published_at) - new Date(a.published_at);

// Client-portal and kiosk sessions don't get announcements — including the
// timed Training kiosk, where a popup would interrupt a test on a shared device.
const isInternalSession = (user) => {
  if (!user || !user.id || user.userType === 'client' || user.trainingKioskUser) return false;
  try {
    const token = localStorage.getItem('token');
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.type !== 'kiosk' && payload.userType !== 'client';
  } catch {
    return false;
  }
};

export const AnnouncementProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const enabled = isInternalSession(currentUser);

  const [announcements, setAnnouncements] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [modal, setModal] = useState({ open: false, selectedId: null });
  const autoOpened = useRef(new Set());

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const data = await api.getFeed();
      setAnnouncements(data.announcements || []);
      setIsAdmin(!!data.isAdmin);
      setLoaded(true);
    } catch (err) {
      console.warn('Announcements: failed to load feed', err?.message || err);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setAnnouncements([]);
      setIsAdmin(false);
      setLoaded(false);
      return undefined;
    }
    refresh();
    const timer = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);

    const socketURL = process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : 'https://ksp3plhq.com';
    const socket = io(socketURL, {
      transports: ['websocket', 'polling'],
      reconnectionDelay: 2000,
      reconnectionDelayMax: 30000,
      withCredentials: true,
    });
    socket.on('announcements-updated', refresh);
    socket.io.on('reconnect', refresh);

    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      socket.disconnect();
    };
  }, [enabled, currentUser?.id, refresh]);

  const patchLocal = (id, fields) =>
    setAnnouncements((list) => list.map((a) => (a.id === id ? { ...a, ...fields } : a)));

  const markViewed = useCallback((id) => {
    const target = announcements.find((a) => a.id === id);
    if (!target || target.viewed_at) return;
    patchLocal(id, { viewed_at: new Date().toISOString() });
    api.markViewed(id).catch((err) => console.warn('Announcements: mark viewed failed', err?.message));
  }, [announcements]);

  const openModal = useCallback((id = null) => setModal({ open: true, selectedId: id }), []);
  const closeModal = useCallback(() => setModal((m) => ({ ...m, open: false })), []);
  const selectInModal = useCallback((id) => setModal((m) => ({ ...m, selectedId: id })), []);

  const derived = useMemo(() => {
    const live = announcements.filter((a) => a.is_live).sort(byPriority);
    const unseen = live.filter((a) => !a.viewed_at);
    // Unseen ones rotate. Once everything's been seen, the top live one stays
    // put until it expires or is archived — nobody can dismiss announcements.
    const bannerItems = unseen.length ? unseen : live.slice(0, 1);
    return { live, unseen, bannerItems };
  }, [announcements]);

  // An urgent announcement nobody has opened yet pops the modal by itself.
  useEffect(() => {
    if (modal.open) return;
    const urgent = derived.unseen.find((a) => a.level === 'urgent' && !autoOpened.current.has(a.id));
    if (urgent) {
      autoOpened.current.add(urgent.id);
      openModal(urgent.id);
    }
  }, [derived.unseen, modal.open, openModal]);

  const value = {
    enabled,
    loaded,
    isAdmin,
    announcements,
    ...derived,
    refresh,
    markViewed,
    modal,
    openModal,
    closeModal,
    selectInModal,
  };

  return <AnnouncementContext.Provider value={value}>{children}</AnnouncementContext.Provider>;
};

export const useAnnouncements = () => {
  const ctx = useContext(AnnouncementContext);
  if (!ctx) throw new Error('useAnnouncements must be used within an AnnouncementProvider');
  return ctx;
};
